'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Shared loading-state primitive used by every fetch-driven block.
 * Renders N placeholder cards with the same outer shape as the real
 * loaded row, so the loaded → real transition doesn't shift the page
 * layout.
 *
 * <p>All colors come from CSS variables via the global
 * {@code .theme-skeleton} class (host-provided) — works in both
 * light and dark themes without overrides. Hosts should honour
 * {@code prefers-reduced-motion} by suppressing the keyframe in their
 * global stylesheet.
 *
 * <h3>Layout modes</h3>
 * <ul>
 *   <li>{@code rail} — horizontal scroll-snap row,
 *       {@code gridAutoFlow: column}. For poster/card rails.</li>
 *   <li>{@code grid} — responsive grid via {@code auto-fit}. For tile
 *       grids.</li>
 *   <li>{@code list} — vertical list of stacked rows. For articles,
 *       events, discussions.</li>
 * </ul>
 */
interface SkeletonRowProps {
  /** Number of placeholders to render. */
  count?: number;
  /** Layout mode — picks the outer container shape. */
  layout?: 'rail' | 'grid' | 'list';
  /** Aspect ratio for the image area, CSS {@code aspect-ratio} syntax. */
  aspect?: string;
  /** Min card width for grid/rail modes. Defaults to {@code 220px}. */
  minCardWidth?: number;
  /** Whether to show the title line + meta line under each card. */
  showText?: boolean;
}

export function SkeletonRow({
  count = 6,
  layout = 'rail',
  aspect = '16 / 9',
  minCardWidth = 220,
  showText = true,
}: SkeletonRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const containerStyle: CSSProperties =
    layout === 'rail'
      ? {
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: `clamp(${Math.max(140, minCardWidth - 60)}px, 22vw, ${minCardWidth}px)`,
          gap: 'clamp(10px, 1.5vw, 16px)',
          overflowX: 'hidden',
        }
      : layout === 'grid'
        ? {
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
            gap: 'clamp(10px, 1.5vw, 16px)',
          }
        : {
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(10px, 1.5vw, 16px)',
          };

  return (
    <div role="status" aria-label={t('shared.skeleton.loading')} style={containerStyle}>
      {Array.from({ length: count }).map((_, i) =>
        layout === 'list' ? (
          <SkeletonListItem key={i} />
        ) : (
          <SkeletonCard key={i} aspect={aspect} showText={showText} />
        ),
      )}
    </div>
  );
}

function SkeletonCard({ aspect, showText }: { aspect: string; showText: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        className="theme-skeleton"
        style={{
          width: '100%',
          aspectRatio: aspect,
          borderRadius: 'var(--radius-md, 8px)',
        }}
      />
      {showText && (
        <>
          <div
            className="theme-skeleton"
            style={{ width: '78%', height: 12, borderRadius: 4, marginTop: 4 }}
          />
          <div className="theme-skeleton" style={{ width: '46%', height: 10, borderRadius: 4 }} />
        </>
      )}
    </div>
  );
}

function SkeletonListItem() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'clamp(10px, 2vw, 16px)',
        padding: 'clamp(8px, 1.5vw, 12px)',
        borderRadius: 'var(--radius-md, 8px)',
        border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
        alignItems: 'center',
      }}
    >
      <div
        className="theme-skeleton"
        style={{
          width: 'clamp(64px, 10vw, 96px)',
          aspectRatio: '4 / 3',
          borderRadius: 'var(--radius-sm, 6px)',
          flex: '0 0 auto',
        }}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="theme-skeleton" style={{ width: '70%', height: 14, borderRadius: 4 }} />
        <div className="theme-skeleton" style={{ width: '90%', height: 10, borderRadius: 4 }} />
        <div className="theme-skeleton" style={{ width: '40%', height: 10, borderRadius: 4 }} />
      </div>
    </div>
  );
}
